import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Modal, Field } from "@/pages/Dashboard";
import { useCurriculum } from "@/context/CurriculumContext";
import ClassHeatmap from "@/components/ClassHeatmap";
import { BandPill } from "@/components/Bands";
import { openHelpGuide } from "@/lib/help";
import { ReviewBadge } from "@/components/ReviewBadge";
import { SUBJECT_META, overallBand } from "@/lib/scale";
import { Plus, ChevronLeft, Loader2, UserPlus, Target, Trash2, FileBarChart, FileEdit, UserPlus2, Grid3x3, Pencil, Settings2, Map, HelpCircle, Layers } from "lucide-react";
import ShareClassModal from "@/components/ShareClassModal";

export default function ClassView() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const curriculum = useCurriculum();
  const [students, setStudents] = useState(null);
  const [cls, setCls] = useState(null);
  const [open, setOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [editClass, setEditClass] = useState(false);
  const [renameStudent, setRenameStudent] = useState(null);
  const [name, setName] = useState("");
  const [isAsd, setIsAsd] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/students?class_id=${classId}`).then((r) => setStudents(r.data));
    api.get("/classes").then((r) => setCls(r.data.find((c) => c.id === classId)));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [classId]);

  const levels = curriculum?.class_levels || ["Junior Infants", "Senior Infants", "1st Class", "2nd Class"];

  const addStudent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/students", { name, class_id: classId, is_asd: isAsd });
      toast.success("Student added");
      setOpen(false);
      setName("");
      setIsAsd(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeStudent = async (id, sName) => {
    if (!window.confirm(`Remove ${sName}? All their assessments and targets will be deleted.`)) return;
    await api.delete(`/students/${id}`);
    toast.success("Student removed");
    load();
  };

  const saveClass = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/classes/${classId}`, { name: cls.name, level: cls.level, year: cls.year || "" });
      toast.success("Class updated");
      setEditClass(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const saveStudentName = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/students/${renameStudent.id}`, { name: renameStudent.name, is_asd: !!renameStudent.is_asd });
      toast.success("Student updated");
      setRenameStudent(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <button
        data-testid="back-to-dashboard"
        onClick={() => navigate("/")}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition mb-5"
      >
        <ChevronLeft size={16} /> All classes
      </button>

      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{cls?.level}{cls?.year ? ` · ${cls.year}` : ""}</p>
          <h1 className="font-['Outfit'] text-4xl font-bold text-slate-800 tracking-tight mt-1">
            {cls?.name || "Class"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="share-class-btn"
            onClick={() => setShareOpen(true)}
            className="bg-white border border-slate-200 hover:border-[#9A7AA0] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <UserPlus2 size={18} /> Share
          </button>
          <button
            data-testid="class-continuum-btn"
            onClick={() => navigate(`/class/${classId}/continuum`)}
            className="text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2 shadow-sm hover:shadow-md"
            style={{ background: "linear-gradient(135deg,#7FB069,#5d8a5a)" }}
          >
            <Layers size={18} /> Continuum
          </button>
          <button
            data-testid="planning-btn"
            onClick={() => navigate(`/class/${classId}/planning`)}
            className="bg-white border border-slate-200 hover:border-[#6C8EBF] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <FileEdit size={18} /> Planning
          </button>
          <button
            data-testid="class-summary-btn"
            onClick={() => navigate(`/class/${classId}/summary`)}
            className="bg-white border border-slate-200 hover:border-[#81B29A] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <FileBarChart size={18} /> Class Summary
          </button>
          <button
            data-testid="numeracy-gaps-btn"
            onClick={() => navigate(`/class/${classId}/gaps`)}
            className="bg-white border border-slate-200 hover:border-[#5d8a73] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <Grid3x3 size={18} /> Gap Analysis
          </button>
          <button
            data-testid="class-map-btn"
            onClick={() => navigate(`/class/${classId}/map`)}
            className="bg-white border border-slate-200 hover:border-[#44546A] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <Map size={18} /> Map
          </button>
          {!cls?.shared && (
            <button
              data-testid="edit-class-btn"
              onClick={() => setEditClass(true)}
              className="bg-white border border-slate-200 hover:border-[#44546A] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
            >
              <Settings2 size={18} /> Edit
            </button>
          )}
          <button
            data-testid="add-student-btn"
            onClick={() => setOpen(true)}
            className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition shadow-sm hover:shadow-md flex items-center gap-2"
          >
            <UserPlus size={18} /> Add Student
          </button>
        </div>
      </div>

      {students === null && (
        <div className="flex justify-center py-20 text-slate-300"><Loader2 className="animate-spin" /></div>
      )}
      {students !== null && students.length === 0 && (
        <div className="bg-white rounded-3xl border border-slate-100 p-12 text-center shadow-[0_4px_24px_rgba(42,59,71,0.04)]">
          <h3 className="font-['Outfit'] text-xl font-bold text-slate-800">No students yet</h3>
          <p className="text-slate-500 mt-2">Add your first student to begin assessing.</p>
          <button data-testid="empty-add-student-btn" onClick={() => setOpen(true)} className="mt-5 bg-[#4A5D6B] text-white rounded-full px-6 py-2.5 font-semibold font-['Outfit'] inline-flex items-center gap-2">
            <Plus size={18} /> Add a student
          </button>
          <button
            data-testid="empty-need-help-btn"
            onClick={openHelpGuide}
            className="mt-4 mx-auto flex items-center gap-1.5 text-sm font-semibold font-['Outfit'] text-[#5d8a73] hover:text-[#3f6b54] transition"
          >
            <HelpCircle size={15} /> Need a hand? See how it works
          </button>
        </div>
      )}
      {students !== null && students.length > 0 && (
        <div className="space-y-3">
          <ClassHeatmap classId={classId} refreshKey={students.length} />
          {students.map((s) => (
            <div
              key={s.id}
              data-testid={`student-row-${s.id}`}
              className="group bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-4 sm:p-5 flex items-center gap-4 hover:shadow-[0_8px_30px_rgba(42,59,71,0.08)] transition-all cursor-pointer"
              onClick={() => navigate(`/student/${s.id}`)}
            >
              <span className="h-12 w-12 rounded-2xl bg-[#4A5D6B] text-white flex items-center justify-center font-['Outfit'] font-bold text-lg shrink-0">
                {initials(s.name)}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="font-['Outfit'] font-semibold text-slate-800 truncate">{s.name}</h3>
                <div className="flex items-center gap-2 mt-1 text-xs text-slate-400 flex-wrap">
                  {s.priority_target_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-[#b07d3a]">
                      <Target size={12} /> {s.priority_target_count} priority {s.priority_target_count === 1 ? "target" : "targets"}
                    </span>
                  )}
                  {s.priority_target_count === 0 && <span>No active targets</span>}
                  <ReviewBadge review={s.review} testId={`review-badge-${s.id}`} />
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                {Object.keys(SUBJECT_META).map((subj) => {
                  const latest = s.latest?.[subj];
                  return (
                    <div key={subj} className="text-center w-24">
                      <p className="text-[10px] uppercase tracking-wide text-slate-300 font-bold mb-1">{SUBJECT_META[subj].name.split(" ")[0]}</p>
                      {latest ? (
                        <BandPill bandKey={latest.band} label={`${latest.total}/45`} />
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid={`rename-student-${s.id}`}
                  onClick={(e) => { e.stopPropagation(); setRenameStudent({ id: s.id, name: s.name, is_asd: !!s.is_asd }); }}
                  className="h-9 w-9 rounded-full text-slate-300 hover:text-[#44546A] hover:bg-slate-100 flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                >
                  <Pencil size={15} />
                </button>
                <button
                  data-testid={`delete-student-${s.id}`}
                  onClick={(e) => { e.stopPropagation(); removeStudent(s.id, s.name); }}
                  className="h-9 w-9 rounded-full text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <Modal title="Add a student" onClose={() => setOpen(false)}>
          <form onSubmit={addStudent} className="space-y-4">
            <Field label="Student name">
              <input
                data-testid="student-name-input"
                required
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Saoirse N."
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A] focus:ring-2 focus:ring-[#81B29A]/40 transition"
              />
            </Field>
            <label data-testid="student-asd-checkbox" className="flex items-start gap-3 cursor-pointer rounded-xl border border-slate-200 p-3.5 hover:border-[#3E9B96] transition">
              <input
                type="checkbox"
                checked={isAsd}
                onChange={(e) => setIsAsd(e.target.checked)}
                className="mt-0.5 h-5 w-5 accent-[#3E9B96] cursor-pointer"
              />
              <span className="text-sm">
                <span className="font-semibold text-slate-700 font-['Outfit']">Autism Support &amp; Development Profile (ASDP)</span>
                <span className="block text-slate-500 mt-0.5">Adds an optional, neurodiversity-affirming developmental category for this pupil.</span>
              </span>
            </label>
            <button
              data-testid="save-student-btn"
              disabled={saving}
              className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-3 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 size={18} className="animate-spin" />} Add student
            </button>
          </form>
        </Modal>
      )}

      {editClass && cls && (
        <Modal title="Edit class" onClose={() => setEditClass(false)}>
          <form onSubmit={saveClass} className="space-y-4">
            <Field label="Class name">
              <input data-testid="edit-class-name-input" required value={cls.name}
                onChange={(e) => setCls({ ...cls, name: e.target.value })}
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]" />
            </Field>
            <Field label="Class level">
              <select data-testid="edit-class-level-select" value={cls.level}
                onChange={(e) => setCls({ ...cls, level: e.target.value })}
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]">
                {levels.map((l) => <option key={l}>{l}</option>)}
              </select>
            </Field>
            <Field label="School year (optional)">
              <input data-testid="edit-class-year-input" value={cls.year || ""}
                onChange={(e) => setCls({ ...cls, year: e.target.value })} placeholder="e.g. 2025/26"
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]" />
            </Field>
            <button data-testid="save-class-btn" disabled={saving}
              className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-3 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={18} className="animate-spin" />} Save changes
            </button>
          </form>
        </Modal>
      )}

      {renameStudent && (
        <Modal title="Edit student" onClose={() => setRenameStudent(null)}>
          <form onSubmit={saveStudentName} className="space-y-4">
            <Field label="Student name">
              <input data-testid="rename-student-input" required autoFocus value={renameStudent.name}
                onChange={(e) => setRenameStudent({ ...renameStudent, name: e.target.value })}
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 outline-none focus:border-[#81B29A]" />
            </Field>
            <label data-testid="rename-student-asd-checkbox" className="flex items-start gap-3 cursor-pointer rounded-xl border border-slate-200 p-3.5 hover:border-[#3E9B96] transition">
              <input
                type="checkbox"
                checked={!!renameStudent.is_asd}
                onChange={(e) => setRenameStudent({ ...renameStudent, is_asd: e.target.checked })}
                className="mt-0.5 h-5 w-5 accent-[#3E9B96] cursor-pointer"
              />
              <span className="text-sm">
                <span className="font-semibold text-slate-700 font-['Outfit']">Autism Support &amp; Development Profile (ASDP)</span>
                <span className="block text-slate-500 mt-0.5">Track this pupil across 5 developmental domains on a 1–9 scale.</span>
              </span>
            </label>
            <button data-testid="save-rename-student-btn" disabled={saving}
              className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-3 font-semibold font-['Outfit'] flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={18} className="animate-spin" />} Save
            </button>
          </form>
        </Modal>
      )}

      {shareOpen && <ShareClassModal classId={classId} className={cls?.name} onClose={() => setShareOpen(false)} />}
    </div>
  );
}

function initials(name) {
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}
